import { useState, DragEvent } from "react";
import { FolderOpen, FileText } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import BrandPane from "./BrandPane";
import NavigationPane from "./NavigationPane";

const AssetPage = () => {
    const [assets, setAssets] = useState({
        referenceFiles: [{ name: "ozempic_pi.pdf", path: "/ozempic_pi.pdf" }],
        preApprovedFiles: [
            { name: "ozempic_faq.pdf", path: "/ozempic_faq.pdf" },
        ],
        draftFiles: [
            { name: "marketing_content.pdf", path: "/marketing_content.pdf" },
        ],
    });

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    };

    const handleDrop =
        (category: "referenceFiles" | "preApprovedFiles" | "draftFiles") =>
        (e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            const files = Array.from(e.dataTransfer.files).map((file) => ({
                name: file.name,
                path: URL.createObjectURL(file),
            }));
            setAssets((prev) => ({
                ...prev,
                [category]: [...prev[category], ...files],
            }));
        };

    return (
        <div className="flex h-screen">
            {/* Brand Pane */}
            <BrandPane />

            {/* Navigation Pane */}
            <NavigationPane />

            {/* Main Content */}
            <div className="flex-1 overflow-auto">
                {/* Header */}
                <div className="p-6 flex justify-end bg-white">
                    <img
                        src="/synthio_logo_with_text.png"
                        alt="SynthioLabs Logo"
                        className="h-8 w-auto"
                    />
                </div>
                <h1 className="text-2xl font-bold px-6 mb-6">Brand Assets</h1>

                {/* Upload Cards */}
                <div className="max-w-7xl mx-auto grid grid-cols-3 gap-6 mb-8 px-6">
                    {[
                        {
                            title: "Reference Materials",
                            category: "referenceFiles",
                        },
                        {
                            title: "Pre-approved Content",
                            category: "preApprovedFiles",
                        },
                        { title: "Content Drafts", category: "draftFiles" },
                    ].map((section) => (
                        <Card
                            key={section.category}
                            className="bg-white"
                            onDragOver={handleDragOver}
                            onDrop={handleDrop(
                                section.category as
                                    | "referenceFiles"
                                    | "preApprovedFiles"
                                    | "draftFiles"
                            )}
                        >
                            <CardContent className="p-6">
                                <div className="flex flex-col items-center justify-center h-40 border-2 border-dashed border-gray-300 rounded-lg">
                                    <FolderOpen
                                        size={40}
                                        className="text-blue-600 mb-2"
                                    />
                                    <p className="text-lg font-medium mb-1">
                                        {section.title}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        Drag and drop files here
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Asset Summary */}
                <div className="space-y-6">
                    {Object.entries(assets).map(([category, files]) => (
                        <div key={category} className="bg-white rounded-lg p-6">
                            <h2 className="text-lg font-semibold mb-4">
                                {category === "referenceFiles" &&
                                    "Reference Materials"}
                                {category === "preApprovedFiles" &&
                                    "Pre-approved Content"}
                                {category === "draftFiles" && "Content Drafts"}
                            </h2>
                            {files.length === 0 ? (
                                <p className="text-gray-500">
                                    No files uploaded yet
                                </p>
                            ) : (
                                <div className="flex space-x-4 overflow-x-auto">
                                    {files.map((file, index) => (
                                        <div
                                            key={index}
                                            className="min-w-[120px] max-w-[120px]"
                                        >
                                            <p className="text-gray-600 mb-2 flex items-start text-xs">
                                                <FileText
                                                    size={12}
                                                    className="mr-1 flex-shrink-0 mt-1"
                                                />
                                                <span className="break-all">
                                                    {file.name}
                                                </span>
                                            </p>
                                            <iframe
                                                src={file.path}
                                                className="w-[80px] h-[80px] border border-gray-200 rounded"
                                                title={file.name}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AssetPage;
